import { useMemo } from 'react'
import { createBrowserRouter } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Home, NotFound, Privacy, Terms } from './pages'
import LandingLayout from './Layout'
import { FlagsProvider } from './context/FlagsContext'
import {
  ErrorBoundary,
  ErrorFallbackGeneric,
  RouteErrorBoundary,
  createErrorBoundaryProps,
  createQueryClientConfig,
} from '@/components/ErrorBoundary'

const queryClient = new QueryClient(
  createQueryClientConfig({ staleTime: 10 * 60 * 1000 })
)

type LandingAppProps = {
  brokerPortalEnabled?: boolean
}

export default function LandingApp({
  brokerPortalEnabled = false,
}: LandingAppProps) {
  const flags = useMemo(() => ({ brokerPortalEnabled }), [brokerPortalEnabled])

  const router = createBrowserRouter([
    {
      path: '/',
      element: <LandingLayout />,
      errorElement: (
        <RouteErrorBoundary FallbackComponent={ErrorFallbackGeneric} />
      ),
      HydrateFallback: () => null,
      children: [
        { index: true, element: <Home /> },
        { path: 'privacy', element: <Privacy /> },
        { path: 'terms', element: <Terms /> },
        { path: '*', element: <NotFound /> },
      ],
    },
  ])

  return (
    <ErrorBoundary
      {...createErrorBoundaryProps(ErrorFallbackGeneric, {
        app: 'landing',
      })}
    >
      <QueryClientProvider client={queryClient}>
        <FlagsProvider flags={flags}>
          <RouterProvider router={router} />
        </FlagsProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
